import api from "../configurations/axios";
import UserDetails from "../models/UserDetails";
import UserDetailsResponse from "../responses/UserDetailsResponse";
import UpdateUserRequest from "../requests/UpdateUserRequest";
import UserFreeEntriesAndCreditBalanceResponse from "../responses/UserFreeEntriesAndCreditBalanceResponse";
import AddUserFreeEntriesRequest from "../requests/AddUserFreeEntriesRequest";
import AddUserCreditBalanceRequest from "../requests/AddUserCreditBalanceRequest";
import SendResetPasswordEmailResponse from "../responses/SendResetPasswordEmailResponse";
import AddUserNoteRequest from "../requests/AddUserNoteRequest";
import UserNotes from "../models/UserNotes";
import AddUserNoteRespone from "../responses/AddUserNoteResponse";
import GetUserNotesResponse from "../responses/GetUserNotesResponse";
import { formatUserNoteDateTime } from "../utils/datetime-helper";
import UserFreeEntryAndCreditTransactionResponse from "../responses/UserFreeEntryAndCreditTransactionResponse";
import GetSetPasswordLinkResponse from "../responses/GetSetPasswordLinkResponse";


class UserService {
  async getUserDetails(userDetails: UserDetails) {
    try {
      const response = await api.get("/api/users/search", {
        params: {
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          emailAddress: userDetails.emailAddress,
          phoneNumber: userDetails.phoneNumber,
          userId: userDetails.userId
        },
      } as any);
      return response;
    } catch (error) {
      console.error("error:", error.response?.data || error.message);
    }
  }

  async getUserByUserId(userId: number): Promise<UserDetailsResponse> {
    try {
      const response = await api.get<UserDetailsResponse>(
        `/api/users/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("error:", error.response?.data || error.message);
    }
  }

  async updateUserDetails(updateUserRequest: UpdateUserRequest) {
    try {
      const response = await api.post("/api/users/update", updateUserRequest);
      return response.data;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      return error.response?.data.errors[0];
    }
  }

  async getUserFreeEntriesAndCreditBalance(userId: number) {
    try {
      const response = await api.get<UserFreeEntriesAndCreditBalanceResponse>(
        "/api/users/free-entry-and-credit-balance",
        {
          params: {
            userId: userId,
          },
        } as any
      );
      return response.data;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      return error.response?.data.errors[0];
    }
  }

  async addOrUpdateUserCreditBalance(request: AddUserCreditBalanceRequest) {
    try {
      const response = await api.post("api/users/credit-balance", request);
      return response.data;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      return error.response?.data.errors[0];
    }
  }

  async addOrUpdateUserFreeEntries(request: AddUserFreeEntriesRequest) {
    try {
      const response = await api.post("api/users/free-entries", request);
      return response.data;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      return error.response?.data.errors[0];
    }
  }

  async sendResetPasswordEmail(userId: number): Promise<SendResetPasswordEmailResponse> {
    try {
      const response = await api.post<SendResetPasswordEmailResponse>(`/api/users/${userId}/forgotten-password`);
      return response.data;
    } catch (error) {
      console.error("error:", error.response?.data || error.message);
      return error.response?.data.errors[0];
    }
  }

  async insertUserNote(addUserNoteRequest: AddUserNoteRequest) {
    try {
      const response = await api.post<AddUserNoteRespone>(
        "api/users/add-note",
        addUserNoteRequest
      );
      return response.data;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      throw error;
    }
  }

  async getUserNotes(userId: number): Promise<GetUserNotesResponse> {
    try {
      const response = await api.get(`/api/users/${userId}/notes`);
      var data = response.data;

      const formattedNotes: UserNotes[] = data.userNotes.map(
        (note: UserNotes) => ({
          noteText: note.noteText,
          customerServiceStaffName: note.customerServiceStaffName,
          createDate: formatUserNoteDateTime(note.createDate),
        })
      );

      const result: GetUserNotesResponse = {
        userNotes: formattedNotes,
        isSuccess: data.isSuccess,
        message: data.message,
      };

      return result;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      throw error;
    }
  }

  async getUserFreeEntryAndCreditTransactions(userId: number): Promise<UserFreeEntryAndCreditTransactionResponse> {
    try {
      const response = await api.get<UserFreeEntryAndCreditTransactionResponse>(
        "/api/users/free-entry-and-credit-transactions",
        {
          params: {
            userId: userId,
          },
        }
      );
      console.log("response:", response.data);
      return response.data;
    } catch (error) {
      console.error("error:", error.message || error.response?.data);
      return error.response?.data.errors[0];
    }
  }

  async getSetPasswordLink(userId: number): Promise<GetSetPasswordLinkResponse> {
    try {
      const response = await api.get<GetSetPasswordLinkResponse>(
        `/api/users/${userId}/set-password-link`
      );
      return response.data;
    } catch (error) {
      console.error("error:", error.response?.data || error.message);
      return error.response?.data;
    }
  }
}

export default new UserService();
